import { el } from "./dom.js";

// "What's new" changelog modal.
//
// On boot we compare the running build's version (__APP_VERSION__, injected by
// Vite from package.json) against the last version this browser acknowledged
// (stored in localStorage). If the user has moved up to a newer build, we show a
// modal listing the notes for every release between the two. On a first run (no
// stored version — a new user, or one who predates this feature) we greet them
// once with the newest release's highlights, then record where they are.
//
// To announce a new release: bump "version" in package.json and prepend an entry
// to RELEASES below. That's it — the next time each user opens the app on the new
// build, they'll see the notes once.

const SEEN_KEY = "whatsNewSeenVersion";

// Newest first. `notes` is a list of short, user-facing bullet points.
const RELEASES = [
	{
		version: "2.21.0",
		title: "Flip through the Newsstand",
		notes: [
			"Swipe right to left on Home to open the Newsstand.",
			"Each section (Cover, Flash, For You, Saved, Setup) is now its own page — swipe between them, or tap the tabs. Swipe back on the Cover to return.",
			"Detail pages open again, and the account button is back in the top bar.",
		],
	},
	{
		version: "2.20.0",
		title: "Look and feel",
		notes: [
			"Settings (menu → Settings): what new items start as, and how dates read (21 Oct 2026, Oct 21, 2026 or 2026-10-21).",
			"The menu now lists everything — Search, Newsstand, People, History, Year in review, Wishlist, Collections, Import, Settings.",
			"Tablets and computers get a wider layout with more covers per row.",
			"Better for keyboards and screen readers: every tappable row can be reached with Tab and pressed with Enter; faint text is easier to read.",
			"A welcome card helps a new library get started.",
		],
	},
	{
		version: "2.19.0",
		title: "On your phone",
		notes: [
			"Phone notifications: Account → Phone notifications → Turn on. Release days, new episodes and comics you follow arrive even when the app is closed.",
			"Pick what to hear about, the time of day, quiet hours, or one daily digest instead of one per release.",
			"On iPhone and iPad, add the app to your Home Screen first — Apple only allows notifications there.",
		],
	},
	{
		version: "2.18.0",
		title: "Collections, smarter",
		notes: [
			"Smart collections: filter any list, then “★ Save as collection” — it keeps filling itself (e.g. Games · Finished + #cozy).",
			"Collections show a progress bar of how much you've finished.",
			"Share a collection as a read-only link — anyone can open it, no account needed. Stop sharing any time.",
		],
	},
	{
		version: "2.17.0",
		title: "Per category",
		notes: [
			"Games: “Get it / more” links to Steam, GOG, Epic, PlayStation, Xbox, Nintendo and the official site. Hours played live under Progress.",
			"Game and book series pages show how far you are — “4 of 7 played”, with what's next.",
			"Books: type or scan an ISBN on Add book (📷 Scan ISBN where the browser supports it).",
			"TV and anime: an “On air” sticker, and an “On the air” filter (airing now / still running).",
			"Movies: “More in this series” — add the rest, or the whole series as a collection.",
			"Comics: a weekly pull list on Subscriptions, and run completion on series pages.",
		],
	},
	{
		version: "2.16.0",
		title: "Tracking depth",
		notes: [
			"Progress on books (pages or %), games (hours or %) and comics (issues, pages or %), with bars on Home.",
			"Finished something? “Start again” begins a new pass — every finish counts in History.",
			"Lent something out? Note who has it and pick a reminder day; the bell asks for it back. The wishlist page lists what's lent out.",
			"Price notes on wishlist items, and Share list sends your wishlist as text.",
		],
	},
	{
		version: "2.15.0",
		title: "Bring your history",
		notes: [
			"Import from Goodreads, Letterboxd, IMDb, MyAnimeList or Trakt: Account → Import from another app.",
			"Every entry is matched to the catalog first (IMDb ids, ISBNs and titles + years); untick anything wrong before importing.",
			"Imported items keep their status, rating, review and tags — and History shows when you really added and finished them.",
		],
	},
	{
		version: "2.14.0",
		title: "Looking back",
		notes: [
			"Year in review (Home → Year in review): what you finished, started and added, by category, your top rated, busiest month and awards — with a shareable image.",
			"Month by month: the last twelve months of finishing, adding and average rating.",
			"Download your diary: all of History as a CSV file.",
			"Each item's History now says how many times you really finished it.",
			"Write a review: a longer text beside the short note, on every detail page.",
			"Your own tags (cozy, with friends, rainy day…) on anything, and a “Your tags” filter on every list.",
		],
	},
	{
		version: "2.13.0",
		title: "Newsstand extras",
		notes: [
			"☆ SAVE any Newsstand story to keep it in Clippings.",
			"Newsstand → Setup: switch beats (Games, Film & TV, Anime, Comics, Books) and outlets off.",
			"Detail pages show “In the news”: the latest stories that mention that game, film, show or book.",
			"Optional, off by default: the bell can tell you about news on something in your library (three a day at most). Newsstand → Setup → The bell.",
		],
	},
	{
		version: "2.12.0",
		title: "People in every category",
		notes: [
			"Games now show who made them: director, writers, designers, composer — plus the studio and publisher. Tap one to see everything they made.",
			"Anime shows its creator, director, writer, composer and studio too.",
			"People → search game directors, studios and anime staff, and follow them.",
			"Following someone rings the bell when something new of theirs shows up — an announced game, a new film, series or book.",
			"Every list's filter has “People you follow”: show only the work of someone you follow.",
			"Signed in, your follows and comic subscriptions now sync between devices.",
		],
	},
	{
		version: "2.11.0",
		title: "Quicker everyday",
		notes: [
			"Search your whole library from the new magnifier in the top bar.",
			"Select on any list: pick several items and change their status or ownership, add them to a group or delete them — with Undo.",
			"Deleting something now offers Undo for a few seconds.",
			"🎲 on every list picks something from your backlog when you can't decide.",
			"Home shows “On this day” (what you finished or started on this date in earlier years) and “Still want these?” for things waiting six months or more.",
			"Lists remember their sort and filters; a new “Edited by you” filter finds what you corrected.",
			"Pull down at the top of a screen to sync. A dot on the account icon says when changes are waiting or failed — details in Account → Sync.",
		],
	},
	{
		version: "2.10.0",
		title: "Fixes & tidying",
		notes: [
			"Games announced for “2027” or “Q4 2026” now say exactly that, instead of pretending to a precise day — and the bell only rings once there's a real release day.",
			"Signing in for the first time now moves your TV series and anime to your account too (not only games, books, movies and comics).",
			"Finished TV and anime series are re-checked less often, so the daily refresh is quicker.",
			"Account → Diagnostics keeps a short log of errors on this device — tap Copy log to send it along with a bug report.",
			"Account → Delete account now works (once it's set up on the server) and removes everything, on every device.",
			"Under the hood: rarely used screens load on demand, so the app starts faster.",
		],
	},
	{
		version: "2.9.0",
		title: "The Newsstand",
		notes: [
			"New: the Newsstand — today's news across games, film & TV, anime, comics and books, laid out like a 90s magazine. Open it from the yellow “Today's issue” strip on Home.",
			"The cover story, a headline ticker, News Flash by category, and a For You section with stories about things in your library and people you follow.",
			"Also inside: your subscribed comics, Previews of your own releases for the next two weeks, the Rumour Mill, and your Readers' Chart — your top 5 of the month from History.",
			"Headlines and summaries come from Eurogamer, IGN, GamesRadar, Polygon, Deadline, TVLine, Anime News Network, CBR, Book Riot and Reactor — tap through to read the full story there.",
		],
	},
	{
		version: "2.8.0",
		title: "People",
		notes: [
			"Movies, shows and books now have a People row: the director and screenwriters of a film, the creator and showrunners of a series, a book's author. Tap a name for everything they made.",
			"A person's page lists their films, series or books — newest, oldest or best known — filtered by role, with ✓ for what you own and + Add for the rest.",
			"Follow people: Home → People shows everyone you follow (comic creators included), and lets you search for more.",
			"Credits come from Wikidata (films), TVmaze (TV) and Open Library (books). Games and anime follow next.",
		],
	},
	{
		version: "2.7.0",
		title: "History",
		notes: [
			"New History screen (Home → History, or Account): everything you add, start, finish, own, rate, award and watch is recorded with its date — filter by category or kind, or search a title. It syncs across your devices.",
			"Your past is filled in from what the app already knew: when things were added, first finished, and started for what you're on now (shown slightly dimmed).",
			"Every detail page has a History section — rewatches and rereads finally leave a trace. Tap ⋯ on an entry to change its date or remove it.",
			"Releases tab: games and movies that came out, plus every release, episode and comic notice — look back at what dropped and when.",
			"Notifications tab: the bell's full archive. “Clear all” now only clears the bell — nothing is lost.",
		],
	},
	{
		version: "2.6.0",
		title: "Comic series subscriptions",
		notes: [
			"Subscribe to a comic series — 🔔 on an issue's “Series” row, on a series page, or under Comics → Subscriptions — and the bell tells you when a new issue is announced and when it goes on sale. Annuals and specials of the series are included (switch them off per series).",
			"Upcoming issues of your subscribed series appear in the Release schedule, about a month ahead.",
			"Collected editions: each subscription watches Open Library for new volumes with a title you choose (trades are usually named after the writer, e.g. “X-Men by Jed MacKay”). Open Library is slow to list new books, so this says a volume exists rather than its release day.",
			"New series page: every issue with its on-sale date, what you already have, and + Add.",
		],
	},
	{
		version: "2.5.0",
		title: "What you own of a creator's runs",
		notes: [
			"A creator's runs now show your collected editions: which trades and hardcovers cover each run, a bar of how much you own, and which issues aren't in any collection you know of.",
			"Issue chips are coloured: solid = you own it (in a collection or as a single issue), outlined = in a collection you don't own yet, grey = uncollected / unknown.",
			"“+ Add a collection for this run” opens the collected-edition search for that series — whatever you add gets the run's issues filled in under “Collects”.",
			"↻ Refresh re-reads a creator's issue list from Metron (it also refreshes on its own every two weeks).",
		],
	},
	{
		version: "2.4.0",
		title: "Comic creators",
		notes: [
			"Follow a comic writer or artist — open Comics → “Creators you follow”, or tap a name under “See their runs” on any issue.",
			"A creator's page lists every run they worked on, grouped by series and relaunch, with the issue numbers collapsed into ranges (“#188–193, #197–200”) and who they worked with.",
			"Filter by what they did — art, writing or everything; cover-only credits stay hidden unless you ask. Roles fill in while the page is open (Metron allows only so many look-ups a minute) and are remembered after that.",
			"Issues already in your library are ticked; tap one to open it.",
		],
	},
	{
		version: "2.3.0",
		title: "Comics",
		notes: [
			"New category: Comics — add single issues (search a series, or type “X-Men 188” to jump to an issue) or collected editions (trades, hardcovers, omnibuses), and track them like everything else.",
			"Issues come with their series, cover date, story titles and credits — who wrote it, who drew it, who did the cover.",
			"On a trade, list which issues it collects (“X-Men (2004) #188–199”). This sets up the creator view coming next: follow an artist or writer and see which of their runs you already own.",
			"Filter your comics by edition, or sort them by series to keep a run in issue order.",
			"Comic data comes from Metron (issues) and Open Library (collected editions).",
		],
	},
	{
		version: "2.2.0",
		title: "Movies",
		notes: [
			"New category: Movies — search any film (or paste an IMDb link), add it, and track it like everything else: watch status, ownership, rating, awards, notes, collections and VS Battle.",
			"Each film brings its poster, plot, runtime, age rating, genres, director, writers and lead cast, plus its IMDb rating as a small read-only line.",
			"Films with a release date join the Release schedule and ring the bell on the day; dates are re-checked daily, so a postponement catches up on its own.",
			"Filter your movie list by genre, on top of the usual status and award filters.",
			"Movie data comes from OMDb.",
			"Every category list can now flip between the cover gallery and a list of rows — tap “▦ Gallery” / “☰ List” in the toolbar. The choice applies to all your lists.",
		],
	},
	{
		version: "2.1.0",
		title: "Fresh dates, next episodes, fix-ups",
		notes: [
			"Release dates keep themselves current: games are re-checked daily and TV/anime catalogs refresh weekly (daily while a show is airing), so postponements and new dates reach the Release schedule and the bell on their own.",
			"Home shows “Continue watching” — the next aired episode you haven't seen for every series you're watching, with a Seen button. The Release schedule lists each airing show's next episode, and the bell rings for new ones.",
			"New ⋯ menu on every detail screen: edit the title, year, date or cover in place (refreshes won't overwrite your edits), re-link an item to the right catalog entry when the match was wrong — keeping everything you did — and undo it.",
			"New award “A blast”, a Seen button on released seasons in the series overview, and marking something unseen now drops its rating to 0 stars.",
		],
	},
	{
		version: "2.0.0",
		title: "A whole new look",
		notes: [
			"MediaTracker has been redesigned top to bottom in a bold editorial 'magazine' style — big covers, condensed headline type, colour-coded categories, and frosted-glass cards over a deep colour-bloom background.",
			"Every screen got the treatment: a cover-story home, masonry cover-wall lists, full-bleed detail pages, a shelf view for collections, and a louder VS Battle and stats spread.",
			"New: look up a book series the way you'd look up a TV show. Search a series (even by its popular name) and see every book in order, like episodes — then add them, and they land together in a collection.",
		],
	},
	{
		version: "1.12.1",
		title: "Anime, much better",
		notes: [
			"Anime seasons now combine into one entry (like TV) instead of a separate entry per season, and episode lists load reliably — even for huge series like One Piece.",
			"Genres, demographic, and studio are still pulled in automatically, so the genre filter keeps working.",
			"Adding anything (games, books, TV, anime): tap a search result to expand it and read a summary + details before adding — and long titles are no longer cut off.",
		],
	},
	{
		version: "1.12.0",
		title: "Anime",
		notes: [
			"New category: Anime (from MyAnimeList) — search, add, and rate any season, movie, OVA, or special.",
			"Each entry pulls in its genres, themes, demographic, studio, and source (manga, light novel, original) automatically.",
			"Filter your anime list by genre and demographic, on top of the usual status and award filters.",
			"Drill into the episode list to mark episodes seen and rate them; your entry rating flows down by default.",
			"Group related seasons together with Collections, and pit anime against each other in VS Battle.",
		],
	},
	{
		version: "1.11.0",
		title: "Collections",
		notes: [
			"Groups, reimagined as Collections: a group now appears as a single entry right in its category list, standing in for the items inside it.",
			"Drag-and-drop — long-press an item and drop it onto another to make a collection, or onto an existing one to add it.",
			"Nest collections inside collections (a series within a bigger universe), with stats rolled up across the whole tree.",
			"Pin the collections you want as cards on your home screen.",
			"Open a collection for an at-a-glance overview — progress, average rating, and awards across everything inside.",
			"Books: pick an owned edition's artwork as the book's cover.",
			"New engagement option “Not interested”, and “Haven't started” is now “Planning to start”.",
		],
	},
	{
		version: "1.10.0",
		title: "TV shows",
		notes: [
			"New category: TV series (from TVmaze) — search, add, and rate them.",
			"Drill into seasons and episodes; rate any level and mark episodes seen.",
			"Your series rating flows down to seasons and episodes by default — tweak any one only if you want.",
			"Series join VS Battle, Groups, and the Home “In progress” list too.",
		],
	},
	{
		version: "1.9.0",
		title: "Editions you own",
		notes: [
			"Record the specific editions or versions you own — not just that you've read or played something.",
			"Books: pick the exact publication(s) you have, by publisher, year, format, and ISBN.",
			"Games: pick the editions you own (Standard, Deluxe, Game of the Year…).",
			'Find it under "Editions you own" on a book or game once you\'ve marked it as owned.',
			"Tidier home screen, with the categories now in a slide-out menu (the ☰ button, top-left).",
			"New awards: Classic, Party pick, Feast for the eyes, and Work of art.",
			"Groups are now yours to curate — automatic franchise/author groups have been retired.",
			"Filter your lists by ownership and awards too, not just status — tap “+ Filter”.",
		],
	},
	{
		version: "1.8.0",
		title: "Accounts & cloud sync",
		notes: [
			"Create an account and sign in — your library now lives in the cloud.",
			"Your games and books sync automatically across every device you sign in on.",
			"Fresh look: a warmer brass-and-serif theme throughout the app.",
			"New top bar with quick access to Home and your Account from anywhere.",
			"Back up and restore your whole library from the Account screen.",
		],
	},
];

// Parse "1.8.0" → [1, 8, 0] for ordered comparison. Missing/garbage parts → 0.
function parseVersion(v) {
	return String(v)
		.split(".")
		.map((n) => Number.parseInt(n, 10) || 0);
}

// Returns > 0 if a is newer than b, < 0 if older, 0 if equal.
export function compareVersions(a, b) {
	const pa = parseVersion(a);
	const pb = parseVersion(b);
	const len = Math.max(pa.length, pb.length);
	for (let i = 0; i < len; i++) {
		const diff = (pa[i] || 0) - (pb[i] || 0);
		if (diff !== 0) return diff;
	}
	return 0;
}

// Decide what (if anything) to show, then update the stored marker. Call once on
// boot for a signed-in user. Safe to call when localStorage is unavailable.
export function maybeShowWhatsNew() {
	const current = __APP_VERSION__;
	let seen = null;
	try {
		seen = localStorage.getItem(SEEN_KEY);
	} catch {
		// Private mode / storage disabled — skip silently rather than throw.
		return;
	}

	// First run on this browser (brand-new user, or an existing user who predates
	// this feature): greet them once with the newest release's highlights, then
	// record where they are so future opens only surface what's changed since.
	if (!seen) {
		writeSeen(current);
		if (RELEASES.length > 0) showModal([RELEASES[0]]);
		return;
	}

	if (compareVersions(current, seen) <= 0) return; // same build, or older

	const fresh = RELEASES.filter((r) => compareVersions(r.version, seen) > 0);
	// Record immediately so the modal won't reappear even if they don't click
	// "Got it" (e.g. they reload). Showing is best-effort from here.
	writeSeen(current);
	if (fresh.length > 0) showModal(fresh);
}

function writeSeen(version) {
	try {
		localStorage.setItem(SEEN_KEY, version);
	} catch {
		/* ignore */
	}
}

function showModal(releases) {
	const close = () => {
		document.removeEventListener("keydown", onKey);
		overlay.remove();
	};
	const onKey = (e) => {
		if (e.key === "Escape") close();
	};

	const card = el(
		"div.whatsnew",
		{ role: "dialog", "aria-modal": "true", "aria-label": "What's new" },
		el("h2.whatsnew__title", {}, "What's new"),
		...releases.map((r) =>
			el(
				"section.whatsnew__release",
				{},
				el(
					"div.whatsnew__version",
					{},
					r.title ? `${r.title} · v${r.version}` : `v${r.version}`,
				),
				el(
					"ul.whatsnew__list",
					{},
					...r.notes.map((n) => el("li", {}, n)),
				),
			),
		),
		el(
			"button.btn.btn--primary.whatsnew__dismiss",
			{ type: "button", onclick: close },
			"Got it",
		),
	);

	const overlay = el(
		"div.whatsnew-overlay",
		{
			onclick: (e) => {
				if (e.target === overlay) close(); // click outside the card
			},
		},
		card,
	);

	document.addEventListener("keydown", onKey);
	document.body.appendChild(overlay);
	card.querySelector(".whatsnew__dismiss").focus();
}
